import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { ReportRunFailure } from '@/types/report';

/**
 * The failure/retry log for a report — admin-only.
 *
 * Fetched lazily: `reportId` is null for every report that hasn't failed,
 * so a healthy report costs no request. The list is newest-first and
 * includes retries, which is what makes "this has now failed three times
 * with the same error" visible instead of a guess.
 */
export interface UseReportFailuresResult {
  failures: ReportRunFailure[];
  isLoading: boolean;
  isRetrying: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Re-fire the work that died. Pass null for report-level failures. */
  retry: (subjectId: string | null) => Promise<void>;
}

export function useReportFailures(reportId: string | null | undefined): UseReportFailuresResult {
  const [failures, setFailures] = useState<ReportRunFailure[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!reportId) {
      setFailures([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setFailures(await vnextApiClient.getReportFailures(reportId));
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  const retry = useCallback(
    async (subjectId: string | null) => {
      if (!reportId) {
        return;
      }
      setIsRetrying(true);
      setError(null);
      try {
        await vnextApiClient.retryFailedRun(reportId, subjectId);
        // Refetch rather than patching locally: the attempt row is written
        // server-side and the caller needs the new history, not a guess.
        await fetchOnce();
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        setError(e);
        throw e;
      } finally {
        setIsRetrying(false);
      }
    },
    [reportId, fetchOnce]
  );

  return { failures, isLoading, isRetrying, error, refetch: fetchOnce, retry };
}
